<template>
	<div>
		<q-table
			:title="$t('questions')"
			:data="questions"
			:columns="columns"
			:pagination.sync="server_pagination"
			@request="fetchData"
			:loading="loading"
			row-key="id"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[0]"
			binary-state-sort
			class="q-mt-md"
		>
			<template slot="top" slot-scope="props" :props="props">
				<div class="col" />

				<q-btn
					color="secondary"
					icon="fas fa-plus"
					:label="$t('new_f')"
					@click="addQuestion()"
				/>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th v-for="col in props.cols" :key="col.name" :props="props">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props" :props="props">
				<q-td auto-width key="move" :props="props">
					<q-btn
						flat
						round
						color="primary"
						icon="fas fa-arrow-up"
						@click="moveItem(props, 'up')"
						:disable="props.row.question_order === 1 || loading"
					/>
					<q-btn
						flat
						round
						color="negative"
						icon="fas fa-arrow-down"
						@click="moveItem(props, 'down')"
						:disable="props.row.question_order === questions.length || loading"
					/>
				</q-td>

				<q-td key="question_order" :props="props">
					{{ props.row.question_order }}
					<q-popup-edit
						v-model="props.row.question_order"
						@hide="reorderItems(props.row)"
					>
						<q-input v-model="new_order" type="number" />
					</q-popup-edit>
				</q-td>

				<q-td key="title" :props="props">
					{{
						props.row.title.length > 50
							? `${props.row.title.slice(0, 47)}...`
							: props.row.title
					}}
				</q-td>

				<q-td key="question_type" :props="props">
					{{ $t(getSurveyQuestionType(props.row.question_type)) }}
				</q-td>

				<q-td key="qty_answers" :props="props">
					{{ props.row.qty_answers }}
				</q-td>

				<q-td key="qty_user_answered" :props="props">
					{{ props.row.qty_user_answered }}
				</q-td>

				<q-td key="actions" :props="props">
					<q-btn
						flat
						round
						color="primary"
						icon="fas fa-pencil-alt"
						@click="editQuestion(props.row)"
					/>
					<q-btn
						flat
						round
						color="negative"
						icon="far fa-trash-alt"
						@click="confirmRemoveQuestion(props.row)"
					/>
				</q-td>
			</q-tr>
		</q-table>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="fas fa-chevron-left"
					@click="$router.push({ name: 'admin_surveys' })"
				/>
			</div>

			<div class="col-xs-12 col-sm-3">
				<q-btn
					color="negative"
					class="full-width"
					:label="$t('unpublish')"
					icon="fas fa-ban"
					@click="unpublish()"
					:disable="disableButtons"
				>
					<q-tooltip v-if="disableButtons">
						{{ $t('no_questions_are_defined') }}
					</q-tooltip>
				</q-btn>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="positive"
					class="full-width"
					:label="$t('publish')"
					icon="fas fa-upload"
					@click="publish()"
					:disable="disableButtons"
				>
					<q-tooltip v-if="disableButtons">
						{{ $t('no_questions_are_defined') }}
					</q-tooltip>
				</q-btn>
			</div>
		</div>

		<q-modal
			v-model="show_alternatives"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
			@hide="clearQuestion()"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn
						flat
						round
						dense
						@click="show_alternatives = false"
						icon="fas fa-times"
					/>
					<q-toolbar-title>
						{{ $t('question') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row gutter-sm">
						<div class="col-12">
							<q-select
								v-model="question.question_type"
								:options="
									survey_question_types.map((s) => {
										return { label: this.$t(s.label), value: s.value }
									})
								"
								:error="$v.question.question_type.$error"
								@input="$v.question.question_type.$touch"
								:float-label="$t('question_type')"
							/>
						</div>

						<div class="col-12">
							<p
								class="caption"
								:class="$v.question.title.$error ? 'text-negative' : ''"
							>
								{{ $t('title') }}
							</p>

							<q-editor
								v-model="question.title"
								:error="$v.question.title.$error"
								@input="$v.question.title.$touch"
								:toolbar="[
									['bold', 'italic', 'strike', 'underline'],
									['left', 'center', 'right', 'justify'],
									['link', 'upload'],
									[
										{
											label: null,
											icon: 'fas fa-font',
											fixedLabel: true,
											fixedIcon: true,
											list: 'no-icons',
											options: [
												'p',
												'h1',
												'h2',
												'h3',
												'h4',
												'h5',
												'h6',
												'code',
											],
										},
										{
											label: null,
											icon: 'fas fa-text-height',
											fixedLabel: true,
											fixedIcon: true,
											list: 'no-icons',
											options: [
												'size-1',
												'size-2',
												'size-3',
												'size-4',
												'size-5',
												'size-6',
												'size-7',
											],
										},
										'removeFormat',
									],
									['quote', 'unordered', 'ordered'],
									['custom_btn'],
								]"
								:definitions="{
									bold: { label: null, icon: 'fas fa-bold' },
									italic: { label: null, icon: 'fas fa-italic' },
									strike: { label: null, icon: 'fas fa-strikethrough' },
									underline: { label: null, icon: 'fas fa-underline' },
									left: { label: null, icon: 'fas fa-align-left' },
									center: { label: null, icon: 'fas fa-align-center' },
									right: { label: null, icon: 'fas fa-align-right' },
									justify: { label: null, icon: 'fas fa-align-justify' },
									link: { label: null, icon: 'fas fa-link' },
									upload: {
										tip: null,
										icon: 'fas fa-file-image',
										handler: insertImg,
									},
									removeFormat: { label: null, icon: 'fas fa-eraser' },
									quote: { label: null, icon: 'fas fa-quote-right' },
									unordered: { label: null, icon: 'fas fa-list-ul' },
									ordered: { label: null, icon: 'fas fa-list-ol' },
								}"
							>
								<q-btn
									slot="custom_btn"
									dense
									flat
									color="black"
									icon="fas fa-code"
									label="HTML"
									@click="show_html = true"
								/>
							</q-editor>
						</div>
					</div>

					<q-table
						:data="question.survey_question_answers"
						:title="$t('alternatives')"
						:columns="columns_alternatives"
						:loading="loading"
						row-key="name"
						:no-data-label="$t('no_data_avaliable')"
						:no-results-label="$t('no_results_found')"
						:loading-label="$t('loading_data')"
						:rows-per-page-label="$t('items_per_page')"
						:rows-per-page-options="[0]"
						binary-state-sort
						class="q-my-lg"
						v-if="question.question_type !== 5 && question.question_type"
					>
						<template slot="top" slot-scope="props" :props="props">
							<div
								class="q-table-title"
								:class="
									$v.question.survey_question_answers.$error
										? 'text-negative'
										: ''
								"
							>
								{{ $t('alternatives') }}
							</div>

							<div class="col" />

							<q-btn
								color="secondary"
								icon="fas fa-plus"
								:label="$t('new_f')"
								@click="addAlternative()"
							/>
						</template>

						<q-tr slot="header" slot-scope="props" :props="props">
							<q-th v-for="col in props.cols" :key="col.name" :props="props">
								{{ $t(col.label) }}
							</q-th>
						</q-tr>

						<q-tr slot="body" slot-scope="props" :props="props">
							<q-td key="title" :props="props">
								{{ props.row.title }}
							</q-td>

							<q-td key="actions" :props="props">
								<q-btn
									flat
									round
									color="primary"
									icon="fas fa-pencil-alt"
									@click="editAlternative(props.row)"
								/>
								<q-btn
									flat
									round
									color="negative"
									icon="far fa-trash-alt"
									@click="confirmRemoveAlternative(props.row)"
								/>
							</q-td>
						</q-tr>
					</q-table>

					<div class="row gutter-sm justify-end q-mt-lg">
						<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
							<q-btn
								color="tertiary"
								class="full-width"
								:label="$t('cancel')"
								icon="fas fa-chevron-left"
								@click="show_alternatives = false"
							/>
						</div>

						<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
							<q-btn
								color="secondary"
								class="full-width"
								:label="$t('save')"
								icon="far fa-save"
								@click="saveQuestion()"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-dialog v-model="show_alternative">
			<span slot="title">{{ $t('alternative') }}</span>

			<div slot="body">
				<q-input v-model="alternative.title" :placeholder="$t('alternative')" />
			</div>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn color="secondary" :label="$t('confirm')" @click="saveAlternative()" />
			</template>
		</q-dialog>

		<q-dialog v-model="confirm_remove">
			<span slot="title">{{ $t('remove_item') }}</span>

			<span slot="message">{{ $t('confirm_remove_item') }}</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
				<q-btn color="negative" :label="$t('remove')" @click="removeQuestion()" />
			</template>
		</q-dialog>

		<q-dialog v-model="confirm_remove_alternative">
			<span slot="title">{{ $t('remove_item') }}</span>

			<span slot="message">{{ $t('confirm_remove_item') }}</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn color="primary" :label="$t('cancel')" @click="cancelRemoveAlternative()" />
				<q-btn color="negative" :label="$t('remove')" @click="removeAlternative()" />
			</template>
		</q-dialog>

		<q-modal
			v-model="show_html"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_html = false" icon="fas fa-times" />
					<q-toolbar-title>
						HTML
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row">
						<div class="col-12">
							<q-input
								type="textarea"
								rows="10"
								float-label="HTML"
								v-model="question.title"
							/>
						</div>
					</div>

					<div class="row gutter-sm justify-end q-mt-lg">
						<div class="col-xs-12 col-sm-3">
							<q-btn
								color="tertiary"
								class="full-width"
								:label="$t('close')"
								icon="fas fa-ban"
								@click="show_html = false"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { date } from 'quasar'
import Uploader from 'components/Uploader'
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import { BlobServiceClient } from '@azure/storage-blob'

export default {
	data() {
		return {
			survey_id: 0,
			questions: [],
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: 0,
				descending: false,
				sortBy: 'question_order',
			},
			columns: [
				{
					name: 'move',
					required: true,
					label: '',
					align: 'left',
					field: 'move',
					sortable: false,
				},
				{
					name: 'question_order',
					required: true,
					label: 'order',
					align: 'left',
					field: 'question_order',
					sortable: false,
				},
				{
					name: 'title',
					required: true,
					label: 'title',
					align: 'left',
					field: 'title',
					sortable: false,
				},
				{
					name: 'question_type',
					required: true,
					label: 'question_type',
					align: 'left',
					field: 'question_type',
					sortable: false,
				},
				{
					name: 'qty_answers',
					required: true,
					label: 'alternatives',
					align: 'left',
					field: 'qty_answers',
					sortable: false,
				},
				{
					name: 'qty_user_answered',
					required: true,
					label: 'user_answers',
					align: 'left',
					field: 'qty_user_answered',
					sortable: false,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'right',
					field: 'edit',
					sortable: false,
				},
			],
			question: {
				question_type: null,
				title: '',
				survey_question_answers: [],
			},
			columns_alternatives: [
				{
					name: 'title',
					required: true,
					label: 'title',
					align: 'left',
					field: 'title',
					sortable: false,
				},
				{
					name: 'actions',
					required: false,
					label: '',
					align: 'right',
					field: 'edit',
					sortable: false,
				},
			],
			alternative: {
				title: '',
			},
			loading: false,
			show_alternatives: false,
			show_alternative: false,
			confirm_remove: false,
			confirm_remove_alternative: false,
			show_html: false,
			upload_url: process.env.API_URL + '/files/policy/',
			urls: [],
			cancel_token: '',
			source: '',
			crop_modal: false,
			original_image: '',
			fileUrl: '',
			new_order: '',
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			survey_question_types: 'registration_lists/survey_question_types',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		disableButtons() {
			if (this.questions.length === 0) return true
			else return false
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},
	},

	methods: {
		fetchData() {
			this.loading = true

			this.$axios
				.get(`/admin_surveys/surveys/${this.survey_id}/survey_questions/`, this.getHeader)
				.then((response) => {
					this.questions = response.data.map((d) => {
						return {
							...d,
							move: `${d.id}-${d.question_order}`,
							question_order: d.question_order ? Number(d.question_order) : 0,
							actions: d.id,
						}
					})
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.loading = false
				})
		},

		insertImg() {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = '.png, .jpg, .gif'
			let file

			input.onchange = (_) => {
				const files = Array.from(input.files)
				file = files[0]

				this.uploadFile(file)
			}
			input.click()
		},

		uploadFile(file) {
			let date = moment()

			let fd = new FormData()
			fd.append('filename', file.name)
			fd.append(
				'file_directory',
				`media/html_images/surveys/${date.year()}/${date.month() + 1}/`
			)

			this.$axios
				.post('/api/files/policy/', fd, this.getHeader, { cancelToken: this.source.token })
				.then((response) => {
					return new Promise((resolve) => {
						this.$q.loading.show()

						if (response.data.origin === 'azure') {
							const blobServiceClient = new BlobServiceClient(
								`${response.data.base_url}?${response.data.sas_token}`
							)

							const main = async () => {
								const container_client = blobServiceClient.getContainerClient('')

								const block_blob_client = container_client.getBlockBlobClient(
									response.data.filename
								)
								const upload_blob_response = await block_blob_client.uploadBrowserData(
									file
								)

								let fd_complete = new FormData()
								fd_complete.append('uploaded', true)
								fd_complete.append('fileSize', file.size)
								fd_complete.append('file', response.data.file_id)

								if (this.custom_directory) {
									fd_complete.append('not_signed', true)
									fd_complete.append('bucket_name', 'static')
								}

								this.$axios
									.post('/api/files/complete/', fd_complete, this.getHeader)
									.then((response) => {
										this.fileUrl = response.data.fileUrl
										document.execCommand(
											'insertHTML',
											true,
											'<div><img class="uploadBase" src="' +
												this.fileUrl +
												'" style="max-width: 100%" /></div>'
										)
									})
									.finally(() => {
										resolve()
										this.$q.loading.hide()
									})
							}

							main()
						} else {
							let content_type =
								file.type != '' ? file.type : 'application/octet-stream'
							let policy = response.data
							let file_name = policy.filename
							let key_path = policy.file_bucket_path

							let fd_s3 = new FormData()
							fd_s3.append('key', key_path + file_name)
							fd_s3.append('acl', 'private')
							fd_s3.append('Content-Type', content_type)
							fd_s3.append('AWSAccessKeyId', policy.key)
							fd_s3.append('Policy', policy.policy)
							fd_s3.append('filename', file_name)
							fd_s3.append('Signature', policy.signature)
							fd_s3.append('file', file)

							this.cancel_token = this.$axios.CancelToken
							this.source = this.cancel_token.source()

							this.$axios
								.post(process.env.BUCKET_URL, fd_s3, {
									// onUploadProgress: function( event ) {
									//     updateProgress(event.loaded / event.total)
									// },
									// cancelToken: this.source.token
								})
								.then((response) => {
									let fd_complete = new FormData()
									fd_complete.append('uploaded', true)
									fd_complete.append('fileSize', file.size)
									fd_complete.append('file', policy.file_id)

									this.$axios
										.post('/api/files/complete/', fd_complete, this.getHeader)
										.then((response) => {
											let data = {
												file_url: response.data.fileUrl,
											}

											this.$axios
												.post('/utils/presigned_url/', data, this.getHeader)
												.then((response) => {
													document.execCommand(
														'insertHTML',
														true,
														`<div><img class="uploadBase" src="${
															response.data.file_url
														}" style="max-width: 100%" /></div>`
													)
												})
												.catch((e) => {
													console.log(e)
												})
												.finally(() => {
													this.$q.loading.hide()
												})
										})
										.finally(() => {
											this.$q.loading.hide()
										})
								})
								.finally(() => {
									resolve()
								})
						}
					})
				})
				.catch((e) => {
					console.log(e)
					this.$q.loading.hide()
				})
		},

		saveQuestion() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				let data = {
					question_type: this.question.question_type,
					title: this.question.title,
				}

				if (this.question.question_type !== 5) {
					data.survey_question_answers = this.question.survey_question_answers
				}

				if (this.question.id) {
					this.$axios
						.put(
							`/admin_surveys/surveys/${this.survey_id}/survey_questions/${
								this.question.id
							}/`,
							data,
							this.getHeader
						)
						.then(() => {
							this.$q.notify({
								message: this.$t('question_save_success'),
								type: 'positive',
								icon: 'far fa-save',
							})

							this.fetchData()
						})
						.catch((e) => {
							console.log(e)

							this.$q.notify({
								message: this.$t('question_save_error'),
								type: 'negative',
								icon: 'fas fa-ban',
							})
						})
						.finally(() => {
							this.$q.loading.hide()
							this.show_alternatives = false
						})
				} else {
					this.$axios
						.post(
							`/admin_surveys/surveys/${this.survey_id}/survey_questions/`,
							data,
							this.getHeader
						)
						.then(() => {
							this.$q.notify({
								message: this.$t('question_save_success'),
								type: 'positive',
								icon: 'far fa-save',
							})

							this.fetchData()
						})
						.catch((e) => {
							console.log(e)

							this.$q.notify({
								message: this.$t('question_save_error'),
								type: 'negative',
								icon: 'fas fa-ban',
							})
						})
						.finally(() => {
							this.$q.loading.hide()
							this.show_alternatives = false
						})
				}
			}
		},

		addQuestion() {
			this.show_alternatives = true
		},

		editQuestion(question) {
			this.question = question
			this.show_alternatives = true
		},

		confirmRemoveQuestion(question) {
			this.question = question
			this.confirm_remove = true
		},

		removeQuestion() {
			this.$axios
				.delete(
					`/admin_surveys/surveys/${this.survey_id}/survey_questions/${
						this.question.id
					}/`,
					this.getHeader
				)
				.then(() => {
					this.question = {}

					this.$q.notify({
						message: this.$t('item_removed_success'),
						type: 'positive',
						icon: 'far fa-trash-alt',
					})
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.confirm_remove = false
					this.fetchData()
				})
		},

		clearQuestion() {
			this.$v.$reset()

			this.question = {
				question_type: null,
				title: '',
				survey_question_answers: [],
			}
		},

		addAlternative() {
			this.show_alternative = true
		},

		editAlternative(alternative) {
			this.alternative = alternative
			this.show_alternative = true
		},

		saveAlternative() {
			if (!this.alternative.id && !this.alternative.__index && this.alternative.title) {
				this.question.survey_question_answers.push(this.alternative)
			} else if (
				this.alternative.title === '' &&
				typeof this.alternative.__index != 'undefined'
			) {
				this.question.survey_question_answers.splice(this.alternative.__index, 1)
			}

			this.hideAlternative()
		},

		hideAlternative() {
			this.alternative = {
				title: '',
			}

			this.show_alternative = false
		},

		confirmRemoveAlternative(alternative) {
			this.confirm_remove_alternative = true

			this.alternative = alternative
		},

		removeAlternative() {
			this.question.survey_question_answers.splice(this.alternative.__index, 1)
			this.confirm_remove_alternative = false

			this.hideAlternative()
		},

		cancelRemoveAlternative() {
			this.hideAlternative()

			this.confirm_remove_alternative = false
		},

		publish() {
			this.$q.loading.show()

			this.$axios
				.get(`/admin_surveys/surveys/${this.survey_id}/publish/`, this.getHeader)
				.then(() => {
					this.$router.push({ name: 'admin_surveys' })

					this.$q.notify({
						message: this.$t('survey_published_success'),
						type: 'positive',
						icon: 'far fa-save',
					})
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		unpublish() {
			this.$q.loading.show()

			this.$axios
				.get(`/admin_surveys/surveys/${this.survey_id}/unpublish/`, this.getHeader)
				.then(() => {
					this.$router.push({ name: 'admin_surveys' })

					this.$q.notify({
						message: this.$t('survey_unpublished_success'),
						type: 'positive',
						icon: 'far fa-save',
					})
				})
				.catch((e) => {
					this.loading = false
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		getSurveyQuestionType(type) {
			let obj = this.survey_question_types.find((obj) => {
				return obj.value === type
			})
			if (obj) return obj.label
			else return ''
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		reorderItems(row) {
			if (this.new_order > 0 && this.new_order <= this.questions.length) {
				if (row.question_order > this.new_order) {
					this.questions.forEach((data) => {
						if (
							data.move != row.move &&
							data.question_order < row.question_order &&
							data.question_order >= this.new_order
						) {
							if (data.question_order + 1 <= this.questions.length)
								data.question_order++
						}
					})
				} else if (row.question_order < this.new_order) {
					this.questions.forEach((data) => {
						if (
							data.move != row.move &&
							data.question_order > row.question_order &&
							data.question_order <= this.new_order
						) {
							if (data.question_order - 1 > 0) data.question_order--
						}
					})
				}

				row.question_order = this.new_order
				this.saveQuestionOrdering()
			}
			this.new_order = ''
		},

		moveItem(item, direction) {
			if (direction == 'up' && item.row.question_order > 1) item.row.question_order--
			else if (direction == 'down' && item.row.question_order < this.questions.length)
				item.row.question_order++

			let obj = this.questions.find((obj) => {
				return obj.move !== item.row.move && obj.question_order === item.row.question_order
			})

			if (obj) {
				if (direction == 'up') {
					obj.question_order++
				}
				if (direction == 'down') {
					obj.question_order--
				}
			}

			this.saveQuestionOrdering()
		},

		saveQuestionOrdering() {
			this.questions = this.questions
				.map((q) => ({ ...q, id: q.id, question_order: q.question_order }))
				.sort((a, b) => a.question_order - b.question_order)

			this.loading = true

			const data = {
				questions: this.questions.map((q) => ({
					question_id: q.id,
					question_order: q.question_order,
				})),
			}

			this.$axios
				.put(
					`/admin_surveys/surveys/${this.survey_id}/save_question_ordering/`,
					data,
					this.getHeader
				)
				.then(() => {
					this.$q.notify({
						message: this.$t('configure_edit_success'),
						type: 'positive',
						icon: 'far fa-save',
					})
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.loading = false
				})
		},
	},

	created() {
		this.survey_id = this.$route.params.id
		if (this.survey_id != 0) this.fetchData()
	},

	validations() {
		return {
			question: {
				question_type: { required },
				title: { required },
				survey_question_answers: {
					requiredIf: requiredIf(() => {
						return this.question.question_type !== 5 ? true : false
					}),
				},
			},
		}
	},
}
</script>

<style></style>
